/**
 * `<PrEngagementsPanel>` — the collaborations a PR contact has, with their
 * lifecycle controls, on `/pr/people/:id`.
 *
 * WHY IT LIVES ON THE PERSON PAGE. `/pr` has a person detail page but no
 * engagement detail page, and the board's pipeline rows already navigate to
 * `/pr/people/:outreach_target_id` — so before this panel you could click a
 * COLLABORATION and land on a page that never mentioned it. The panel closes
 * that gap without minting a `/pr/engagements/:id` route that Phase 2's
 * edit/delete work may restructure (invariant 7).
 *
 * It fetches by dispatching the READ action `list_pr_engagements` rather than
 * adding a GET route — the `PurchaseCreditsModal` precedent. That keeps the
 * single-action-layer invariant (one implementation, three surfaces) and means
 * no `server.ts` change ships with this panel.
 *
 * DELIBERATELY NOT OFFERED (recorded so a later session does not "complete" it):
 *   - `message_body` on `record_pr_engagement_event`. It writes a real
 *     `outbound_messages` row and fails `pr_manual_message_unroutable` unless
 *     `channel_used` is the manual-IG shape AND the contact has a handle. It is
 *     a send, not a log; it stays CLI-only.
 *   - a free `stage` picker. See `stageOptionsAfter` — skipping is permitted by
 *     the action and permanently blanks the intermediate stamps the `/pr`
 *     counters are derived from.
 */

import { useCallback, useEffect, useState } from "react";
import { dispatchAction } from "../lib/dispatch";
import { ActionModalButton } from "./ActionModalButton";
import {
  PR_EXCHANGE_KIND_OPTIONS,
  PR_LOG_EVENT_KINDS,
  PR_SESSION_LOCATION_OPTIONS,
  stageOptionsAfter,
  validateCreateEngagementDraft,
  validatePrEventDraft,
} from "../lib/pr-engagement-edit";
import { studioFormat } from "../lib/studio-tz";

/**
 * One row of `list_pr_engagements`.
 *
 * Two wire-shape traps, both found by RENDERING the panel rather than by any
 * test, and both silent — they produce `undefined`, never an error:
 *
 *   - the id on the wire is `pr_engagement_id`. The action's response mapper
 *     renames the DB row's `id`, so reading `.id` here yields `undefined`.
 *     React surfaced that only as a missing-`key` warning, while every button
 *     would have dispatched `pr_engagement_id: undefined` and 400ed.
 *   - `last_event` is a NESTED object `{at, kind, summary}`. The flat
 *     `last_event_at` / `_kind` / `_summary` names exist only on the action's
 *     internal repository row type, never on the wire — reading them yields
 *     `undefined`, and `undefined !== null` rendered an all-dashes line.
 *
 * Both are the same lesson: this row type is hand-written against a dispatch
 * response with no shared type, so it is only as correct as the last person who
 * opened the page.
 */
interface PrEngagementRow {
  readonly pr_engagement_id: string;
  readonly stage: string;
  readonly angle: string;
  readonly exchange_kind: string;
  readonly session_location: string | null;
  readonly closed_reason: string | null;
  readonly next_action_at: string | null;
  readonly timeline_count: number;
  readonly last_event: {
    readonly at: string;
    readonly kind: string;
    readonly summary: string;
  } | null;
}

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmt(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATE_OPTS).format(d);
}

export function PrEngagementsPanel({
  targetId,
  targetType,
}: {
  readonly targetId: string;
  /** `counterparty` rows are inbox-minted and may not hold a PR engagement. */
  readonly targetType: string;
}): JSX.Element {
  const [rows, setRows] = useState<ReadonlyArray<PrEngagementRow> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((): void => {
    void dispatchAction<
      Record<string, unknown>,
      { engagements: ReadonlyArray<PrEngagementRow> }
    >("list_pr_engagements", {
      outreach_target_id: targetId,
      include_closed: true,
      limit: 50,
    }).then((r) => {
      if (r.ok) {
        setRows(r.response.engagements);
        setError(null);
      } else {
        setRows([]);
        setError(`${r.message} (${r.code})`);
      }
    });
  }, [targetId]);

  useEffect(() => {
    load();
  }, [load]);

  const isCounterparty = targetType === "counterparty";

  return (
    <div className="pr-engagements">
      <div className="pr-section-head">
        <h2 className="pr-heading">Collaborations</h2>
        <ActionModalButton
          action="create_pr_engagement"
          buttonLabel="Open a collaboration"
          buttonClassName="row-action-btn"
          buttonTitle={
            isCounterparty
              ? "Counterparties are inbox correspondents, not PR contacts"
              : "Start a new collaboration with this contact"
          }
          disabled={isCounterparty}
          modalTitle="Open a collaboration"
          description="If one is already live for this contact, you'll get that one back rather than a second."
          prefill={{ outreach_target_id: targetId }}
          fields={[
            {
              kind: "textarea",
              name: "angle",
              label: "Angle",
              required: true,
              maxLength: 500,
              placeholder: "what's in it for them, in a sentence",
            },
            {
              kind: "select",
              name: "exchange_kind",
              label: "What we get",
              required: true,
              options: [...PR_EXCHANGE_KIND_OPTIONS],
            },
            {
              kind: "select",
              name: "session_location",
              label: "Session location",
              options: [...PR_SESSION_LOCATION_OPTIONS],
            },
            { kind: "datetime", name: "next_action_at", label: "Next action" },
            { kind: "text", name: "note", label: "Opening note", maxLength: 500 },
          ]}
          validate={validateCreateEngagementDraft}
          submitLabel="Open it"
          onDone={load}
        />
      </div>

      {error !== null ? <p className="flag-card-err">{error}</p> : null}
      {rows === null ? (
        <p className="empty">Loading collaborations…</p>
      ) : rows.length === 0 ? (
        <p className="empty">No collaborations with this contact yet.</p>
      ) : (
        rows.map((e) => (
          <EngagementCard key={e.pr_engagement_id} engagement={e} onChanged={load} />
        ))
      )}
    </div>
  );
}

function EngagementCard({
  engagement: e,
  onChanged,
}: {
  readonly engagement: PrEngagementRow;
  readonly onChanged: () => void;
}): JSX.Element {
  const open = e.stage !== "closed";
  const stageChoices = stageOptionsAfter(e.stage);

  return (
    <div className="pr-engagement-card">
      <div className="pr-engagement-head">
        <span className={`pr-stage-chip${open ? "" : " pr-stage-closed"}`}>
          {e.stage}
        </span>{" "}
        <span className="tag">{e.exchange_kind}</span>{" "}
        <span className="dc-muted">
          {e.timeline_count} events · next {fmt(e.next_action_at)}
        </span>
      </div>
      <p className="pr-engagement-angle">{e.angle}</p>
      {e.last_event !== null ? (
        <p className="pr-sub dc-muted">
          {e.last_event.kind} · {fmt(e.last_event.at)} — {e.last_event.summary}
        </p>
      ) : null}
      {e.closed_reason !== null ? (
        <p className="pr-sub dc-muted">Closed: {e.closed_reason}</p>
      ) : null}

      {open ? (
        <div className="pr-engagement-actions">
          <ActionModalButton
            action="record_pr_engagement_event"
            buttonLabel="Log / advance"
            buttonClassName="row-action-btn"
            buttonTitle="Append an event, and optionally move the stage forward"
            modalTitle="Log an event"
            description="Summaries are reference-only — never paste the correspondence."
            prefill={{ pr_engagement_id: e.pr_engagement_id }}
            fields={[
              {
                kind: "select",
                name: "kind",
                label: "Kind",
                required: true,
                defaultValue: "note",
                options: PR_LOG_EVENT_KINDS.map((k) => ({
                  value: k,
                  label: k,
                })),
              },
              {
                kind: "textarea",
                name: "summary",
                label: "Summary",
                required: true,
                maxLength: 500,
              },
              {
                // Only this stage forward, and never `closed` — see
                // `stageOptionsAfter`. Leaving it blank logs without moving.
                kind: "select",
                name: "stage",
                label: "Move to stage (optional)",
                options: stageChoices.map((s) => ({ value: s, label: s })),
              },
              { kind: "datetime", name: "at", label: "When (defaults to now)" },
              {
                kind: "select",
                name: "session_location",
                label: "Session location",
                options: [...PR_SESSION_LOCATION_OPTIONS],
              },
              {
                kind: "datetime",
                name: "next_action_at",
                label: "Next action",
              },
              {
                kind: "text",
                name: "channel_used",
                label: "Channel used",
                maxLength: 40,
              },
            ]}
            validate={validatePrEventDraft}
            submitLabel="Log it"
            onDone={onChanged}
          />

          <ActionModalButton
            action="record_pr_engagement_event"
            buttonLabel="Follow-up logged"
            buttonClassName="row-action-btn"
            buttonTitle="Stamp a follow-up without moving the pipeline"
            modalTitle="Log a follow-up"
            description="Stamps followed_up_at. The stage does not move."
            prefill={{ pr_engagement_id: e.pr_engagement_id, kind: "followed_up" }}
            fields={[
              {
                kind: "text",
                name: "summary",
                label: "What you chased",
                required: true,
                maxLength: 500,
              },
            ]}
            submitLabel="Log it"
            onDone={onChanged}
          />

          <ActionModalButton
            action="record_pr_engagement_event"
            buttonLabel="Close"
            buttonClassName="row-action-btn row-action-btn-danger"
            buttonTitle="End this collaboration"
            modalTitle="Close this collaboration"
            description="Closing is PERMANENT — the stage machine is forward-only, so re-engaging means opening a new collaboration."
            prefill={{
              pr_engagement_id: e.pr_engagement_id,
              kind: "closed",
              stage: "closed",
            }}
            fields={[
              {
                kind: "text",
                name: "summary",
                label: "Summary",
                required: true,
                maxLength: 500,
              },
              {
                kind: "textarea",
                name: "closed_reason",
                label: "Why is it closed?",
                required: true,
                maxLength: 500,
                placeholder: "delivered · declined · went quiet · …",
              },
            ]}
            submitLabel="Close it"
            onDone={onChanged}
          />
        </div>
      ) : null}
    </div>
  );
}
